// React
import { useState, useMemo, Fragment } from "react"

// GraphQL
import { ProjectType, useFinanceDashboardQuery } from "@/api/graphql"

// Router
import { Link } from "@/lib/router"
import { Routes } from "@/constants/routes"

// UI
import { Button } from "@/components/Button"

import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableHeading,
	TableRowCell,
} from "@/components/table-controls/TableItems"
import {
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	flexRender,
	createColumnHelper,
	CellContext,
} from "@/lib/table"
import { PaginationAsButtons } from "@/components/PaginationAsButtons"

// Translations
import { useTrans } from "@/i18n"
import { useLang } from "@/context/useLang"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

// Icons
import { CurrencyEuroIcon } from "@/lib/icons"

// Context
import { useCurrentUserRoles } from "@/context/user"
import { UserRoles } from "@/constants/constants"

/**
 * FinanceProjectsTable
 *
 * @returns
 */
export function FinanceProjectsTable() {
	// Constants
	const projectsPerPage = 10

	// State
	const [currentPage, setCurrentPage] = useState<number>(0)

	// I18n
	const t = useTrans("finance")
	const { data, isLoading } = useFinanceDashboardQuery({
		limit: projectsPerPage,
		offset: currentPage * projectsPerPage,
	})
	const { formatCurrency } = useLang()
	const DateTime = useLocalizedDateTime()

	// Context
	const { hasRole } = useCurrentUserRoles()

	// Tables
	const columnHelper = createColumnHelper<ProjectType>()
	const columns = useMemo(
		() => [
			columnHelper.accessor("id", {
				id: "project.name",
				header: () => (
					<TableHeading variant="static">
						{t("finance:finance_dashboard.table.project.title")}
					</TableHeading>
				),
				cell: ({ row }) => (
					<TableDataCell className="!p-0">
						<Link
							to={`${Routes.FinanceProjects}/${row.original.id}`}
							className="block h-full px-6 py-4 font-bold text-gray-700 hover:text-gray-900"
						>
							{row.original.name}
						</Link>
					</TableDataCell>
				),
			}),
			columnHelper.accessor(
				(row) => row.fiscal_overview?.at(0)?.value_end,
				{
					id: "loan_outstanding",
					header: () => (
						<TableHeading variant="static">
							{t(
								"finance:finance_dashboard.table.loan_outstanding.title",
							)}
						</TableHeading>
					),
					cell: (info) => (
						<TableDataCell>
							{info.getValue()
								? formatCurrency(Number(info.getValue()) || 0)
								: "-"}
						</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(row): string | null =>
					(row.current_interest_period?.end as string | undefined) ??
					null,
				{
					id: "next_interest_period_end",
					header: () => (
						<TableHeading variant="static">
							{t(
								"finance:finance_dashboard.table.next_interest_period_end.title",
							)}
						</TableHeading>
					),
					cell: (info) => {
						const value = info.getValue()
						return (
							<TableDataCell>
								{value
									? DateTime.fromISO(value).toLocaleString({
											day: "2-digit",
											month: "2-digit",
											year: "numeric",
										})
									: "-"}
							</TableDataCell>
						)
					},
				},
			),
			columnHelper.accessor("project_owner_pay_in_link", {
				id: "project_owner_pay_in_link",
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:finance_dashboard.table.top_up_account.title",
						)}
					</TableHeading>
				),
				cell: (info: CellContext<ProjectType, string>) => (
					<TableDataCell>
						{info.getValue() && (
							<a href={info.getValue() ?? ""}>
								<Button variant="primary" size="small">
									<CurrencyEuroIcon
										aria-hidden="true"
										className="mr-1 size-5"
									/>
									{t(
										"finance:finance_dashboard.table.top_up_account.title",
									)}
								</Button>
							</a>
						)}
					</TableDataCell>
				),
			}),
		],
		[t, formatCurrency, DateTime],
	)
	const table = useReactTable({
		columns,
		data: (data?.me?.finance_projects?.results as ProjectType[]) ?? [],
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		state: {
			pagination: {
				pageIndex: 0,
				pageSize: projectsPerPage,
			},
			columnVisibility: {
				project_owner_pay_in_link:
					hasRole(UserRoles.projectOwner) || hasRole(UserRoles.admin),
			},
		},
	})

	// Data state
	return (
		<>
			<Table
				isLoading={isLoading}
				skeletonRows={10}
				headerGroups={table.getHeaderGroups()}
			>
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => {
						return (
							<TableRowCell key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<Fragment key={header.id}>
											{flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
										</Fragment>
									)
								})}
							</TableRowCell>
						)
					})}
				</TableHead>
				<TableBody>
					{table.getRowModel().rows.map((row, index) => (
						<TableRowCell
							key={row.id}
							isOdd={index % 2 === 0}
							data-testid={`tablerow-${row.original.id}`}
						>
							{row._getAllVisibleCells().map((cell) => (
								<Fragment key={cell.id}>
									{flexRender(
										cell.column.columnDef.cell,
										cell.getContext(),
									)}
								</Fragment>
							))}
						</TableRowCell>
					))}
				</TableBody>
			</Table>

			<div className="mt-6 flex w-full justify-center px-4 py-5 sm:p-6 md:mt-8">
				<PaginationAsButtons
					countPerPage={projectsPerPage}
					totalCount={data?.me?.finance_projects?.totalCount ?? 0}
					itemType={"common:pagination.item_types.project"}
					currentPage={currentPage + 1}
					currentItemsAmount={
						data?.me?.finance_projects?.results?.length ?? 0
					}
					onNextPage={() =>
						setCurrentPage((currentPage) => currentPage + 1)
					}
					onPrevPage={() =>
						setCurrentPage((currentPage) =>
							Math.max(currentPage - 1, 0),
						)
					}
					analyticsId="investments"
				/>
			</div>
		</>
	)
}
